import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import InlineError from '../components/InlineError';
import LoadingSpinner from '../components/LoadingSpinner';
import QRCode from 'react-qr-code';
import { useAuth } from '../contexts/AuthContext';

const CATEGORIES = ['All', 'States', 'Industries', 'Companies'];

function getCategory(name) {
    if (name.startsWith('State: '))    return 'States';
    if (name.startsWith('City: '))     return 'Top Cities';
    if (name.startsWith('Industry: ')) return 'Industries';
    if (name.startsWith('Company: '))  return 'Companies';
    if (name === 'You Bee General Hive') return 'All';
    return 'Companies';
}

const displayName = (name) => name.replace(/^(State|City|Industry|Company): /, '');

// --- Shared modal button styles ---
const optionCardStyle = {
    display: 'flex', alignItems: 'center', gap: '16px',
    padding: '16px 20px', borderRadius: '14px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    cursor: 'pointer', width: '100%', textAlign: 'left',
    transition: 'border-color 0.2s, background 0.2s',
};

const backBtnStyle = {
    background: 'none', border: 'none',
    color: 'rgba(255,255,255,0.5)', cursor: 'pointer',
    fontSize: '0.85rem', padding: 0, alignSelf: 'flex-start',
};

// --- CONTRIBUTE MODAL ---
function ContributeModal({ pool, onClose, onSuccess, solPrice }) {
    const [step, setStep] = useState('choose'); // 'choose' | 'wallet' | 'direct' | 'stripe' | 'contact'
    const [sliderPct, setSliderPct] = useState(50); // 0–99
    const [anonymous, setAnonymous] = useState(false);
    const [donating, setDonating] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [balanceSol, setBalanceSol] = useState(null);
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [stripeLoading, setStripeLoading] = useState(false);
    const [stripeError, setStripeError] = useState('');
    const stripeMountRef = useRef(null);
    const stripeSessionRef = useRef(null);

    // Fetch user balance when wallet step opens
    useEffect(() => {
        if (step !== 'wallet') return;
        setBalanceLoading(true);
        api.get('/balance')
            .then(res => setBalanceSol(res.data.balance ?? 0))
            .catch(() => setBalanceSol(0))
            .finally(() => setBalanceLoading(false));
    }, [step]);

    const [stripeRedirectUrl, setStripeRedirectUrl] = useState(null);

    // Mount embedded Stripe onramp widget when stripe step opens
    useEffect(() => {
        if (step !== 'stripe') return;
        let cancelled = false;
        setStripeLoading(true);
        setStripeError('');
        setStripeRedirectUrl(null);

        (async () => {
            try {
                const [{ loadStripeOnramp }, res, hasAuthenticator] = await Promise.all([
                    import('@stripe/crypto'),
                    api.post('/wallet/onramp-session', { poolWalletAddress: pool.wallet_address }),
                    window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable?.() ?? Promise.resolve(false),
                ]);
                if (cancelled) return;

                if (!hasAuthenticator) {
                    // No platform authenticator (e.g. Windows without Hello) — use redirect
                    setStripeRedirectUrl(res.data.redirectUrl);
                    setStripeLoading(false);
                    return;
                }

                const stripeOnramp = await loadStripeOnramp(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');
                if (cancelled) return;
                const session = stripeOnramp.createSession({
                    clientSecret: res.data.clientSecret,
                    appearance: { theme: 'dark' },
                });
                stripeSessionRef.current = session;
                session.mount(stripeMountRef.current);
                setStripeLoading(false);
                session.addEventListener('onramp_session_updated', (e) => {
                    if (e.payload?.session?.status === 'fulfillment_complete') {
                        setTimeout(onClose, 2000);
                    }
                });
            } catch (err) {
                if (!cancelled) {
                    setStripeError(err.response?.data?.error || err.message || 'Failed to load payment form.');
                    setStripeLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
            if (stripeSessionRef.current) {
                try { stripeSessionRef.current.unmount(); } catch {}
                stripeSessionRef.current = null;
            }
        };
    }, [step, onClose, pool.wallet_address]);

    // balance is USDC; no rent reserve needed for USDC (only SOL reserve matters for fees)
    const maxUsdc = balanceSol !== null ? Math.max(0, balanceSol) : 0; // balanceSol field holds USDC now
    const selectedUsdc = maxUsdc * (sliderPct / 100);

    const handleWalletDonate = async () => {
        if (selectedUsdc <= 0) {
            setError('Please select an amount greater than $0.');
            return;
        }
        setDonating(true);
        setError('');
        try {
            await api.post('/pools/donate', { poolId: pool.id, amount: selectedUsdc, anonymous });
            onSuccess(selectedUsdc);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Contribution failed');
            setDonating(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(pool.wallet_address);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 1000,
                background: 'rgba(0,0,0,0.75)',
                backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '20px',
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: 'linear-gradient(180deg, #1a1a2e 0%, #12121a 100%)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '20px',
                    padding: '32px',
                    width: '100%',
                    maxWidth: '480px',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    position: 'relative',
                }}
            >
                {/* Close button */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute', top: '16px', right: '16px',
                        background: 'rgba(255,255,255,0.08)', border: 'none',
                        borderRadius: '8px', color: 'rgba(255,255,255,0.6)',
                        width: '32px', height: '32px', cursor: 'pointer',
                        fontSize: '1.2rem', lineHeight: 1,
                    }}
                >×</button>

                {/* Header */}
                <div style={{ marginBottom: '24px' }}>
                    <p style={{
                        color: '#ffc107', fontSize: '0.75rem', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 6px',
                    }}>
                        Contribute to Hive
                    </p>
                    <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#fff' }}>
                        {displayName(pool.name)}
                    </h2>
                </div>

                {/* Step: Choose method */}
                {step === 'choose' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <p style={{
                            margin: '0 0 4px', fontSize: '0.88rem',
                            color: 'rgba(255,255,255,0.6)', lineHeight: 1.6,
                        }}>
                            {pool.name === 'You Bee General Hive'
                                ? 'Your contribution goes directly into a universal basic income hive and will be distributed equally to all You Bee worker bees with active accounts.'
                                : 'Your contribution goes directly into a universal basic income hive and will be distributed equally to all You Bee worker bees who qualify based on their profile.'
                            }
                        </p>
                        <button
                            onClick={() => setStep('wallet')}
                            style={optionCardStyle}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,193,7,0.4)'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                        >
                            <span style={{ fontSize: '1.8rem', flexShrink: 0, width: '36px', textAlign: 'center' }}>🐝</span>
                            <div>
                                <div style={{ fontWeight: 700, color: '#fff', fontSize: '1rem' }}>You-Bee Wallet</div>
                                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', marginTop: '2px' }}>
                                    Transfer from your YouBee wallet balance
                                </div>
                            </div>
                        </button>

                        <button
                            onClick={() => setStep('direct')}
                            style={optionCardStyle}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,193,7,0.4)'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                        >
                            <span style={{ fontSize: '1.8rem', flexShrink: 0, width: '36px', textAlign: 'center' }}>◎</span>
                            <div>
                                <div style={{ fontWeight: 700, color: '#fff', fontSize: '1rem' }}>Send USDC via Solana Wallet</div>
                                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', marginTop: '2px' }}>
                                    Send USDC with Phantom, Solflare, or any Solana wallet
                                </div>
                            </div>
                        </button>

                        <button
                            onClick={() => setStep('stripe')}
                            style={optionCardStyle}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,193,7,0.4)'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                        >
                            <span style={{ fontSize: '1.8rem', flexShrink: 0, width: '36px', textAlign: 'center' }}>💳</span>
                            <div>
                                <div style={{ fontWeight: 700, color: '#fff', fontSize: '1rem' }}>Credit Card or Bank Account</div>
                                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', marginTop: '2px' }}>
                                    Contribute via credit or debit card — funds are automatically converted to USDC
                                </div>
                            </div>
                        </button>

                        <button
                            onClick={() => setStep('contact')}
                            style={optionCardStyle}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,193,7,0.4)'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                        >
                            <span style={{ fontSize: '1.8rem', flexShrink: 0, width: '36px', textAlign: 'center' }}>📬</span>
                            <div>
                                <div style={{ fontWeight: 700, color: '#fff', fontSize: '1rem' }}>Contact You Bee</div>
                                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', marginTop: '2px' }}>
                                    For large or unusual contributions — we'll work with you directly
                                </div>
                            </div>
                        </button>
                    </div>
                )}

                {/* Step: You-Bee Wallet */}
                {step === 'wallet' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <button onClick={() => { setStep('choose'); setError(''); setSliderPct(50); }} style={backBtnStyle}>
                            ← Back
                        </button>

                        {/* Balance display */}
                        <div style={{
                            background: 'rgba(255,255,255,0.04)', borderRadius: '12px',
                            padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Your balance</span>
                            <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
                                {balanceLoading ? '…' : balanceSol !== null ? `$${balanceSol.toFixed(2)} USDC` : '…'}
                            </span>
                        </div>

                        {/* Amount slider */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Contribution amount</span>
                                <span style={{ color: '#ffc107', fontWeight: 700, fontSize: '1.3rem' }}>
                                    ${selectedUsdc.toFixed(2)} USDC
                                </span>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={sliderPct}
                                onChange={e => setSliderPct(Number(e.target.value))}
                                disabled={maxUsdc === 0}
                                style={{ width: '100%', accentColor: '#ffc107', cursor: maxUsdc === 0 ? 'not-allowed' : 'pointer' }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>
                                <span>$0</span>
                                <span>{maxUsdc > 0 ? `$${maxUsdc.toFixed(2)} max` : 'No balance'}</span>
                            </div>
                            {maxUsdc === 0 && !balanceLoading && (
                                <p style={{ margin: 0, color: '#ff6b6b', fontSize: '0.82rem' }}>
                                    Your YouBee wallet has no USDC balance to contribute.
                                </p>
                            )}
                        </div>

                        {/* Anonymous checkbox */}
                        <label style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            cursor: 'pointer', userSelect: 'none',
                        }}>
                            <input
                                type="checkbox"
                                checked={anonymous}
                                onChange={e => setAnonymous(e.target.checked)}
                                style={{ width: '16px', height: '16px', accentColor: '#ffc107', cursor: 'pointer' }}
                            />
                            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem' }}>
                                Contribute anonymously
                            </span>
                        </label>

                        {error && (
                            <p style={{ margin: 0, color: '#ff6b6b', fontSize: '0.85rem' }}>{error}</p>
                        )}
                        <button
                            onClick={handleWalletDonate}
                            disabled={donating || maxUsdc === 0 || selectedUsdc === 0}
                            style={{
                                padding: '14px', borderRadius: '12px', border: 'none',
                                background: (donating || maxUsdc === 0 || selectedUsdc === 0)
                                    ? 'rgba(255,193,7,0.3)'
                                    : 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                                color: (donating || maxUsdc === 0 || selectedUsdc === 0) ? 'rgba(0,0,0,0.4)' : '#000',
                                fontWeight: 700, fontSize: '0.95rem',
                                cursor: (donating || maxUsdc === 0 || selectedUsdc === 0) ? 'not-allowed' : 'pointer',
                                boxShadow: (donating || maxUsdc === 0 || selectedUsdc === 0) ? 'none' : '0 4px 20px rgba(255,193,7,0.3)',
                            }}
                        >
                            {donating ? 'Processing…' : `Contribute $${selectedUsdc.toFixed(2)} USDC`}
                        </button>
                    </div>
                )}

                {/* Step: Stripe On-Ramp */}
                {step === 'stripe' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <button onClick={() => setStep('choose')} style={backBtnStyle}>
                            ← Back
                        </button>

                        {stripeLoading && (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: 'rgba(255,255,255,0.4)' }}>
                                Loading secure payment form…
                            </div>
                        )}

                        {stripeError && (
                            <div style={{
                                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                                borderRadius: '12px', padding: '16px', color: '#ef4444',
                                fontSize: '0.9rem', textAlign: 'center',
                            }}>
                                {stripeError}
                            </div>
                        )}

                        {stripeRedirectUrl ? (
                            <>
                                <div style={{
                                    background: 'rgba(255,255,255,0.04)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: '12px', padding: '16px',
                                    fontSize: '0.88rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6,
                                }}>
                                    USDC will be sent directly to the <strong style={{ color: '#fff' }}>{displayName(pool.name)}</strong> hive. Payment is handled securely by Stripe.
                                </div>
                                <a
                                    href={stripeRedirectUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: 'block', padding: '14px', borderRadius: '12px',
                                        background: 'linear-gradient(135deg, #635bff 0%, #7c6aff 100%)',
                                        color: '#fff', fontWeight: 700, fontSize: '0.95rem',
                                        textAlign: 'center', textDecoration: 'none',
                                        boxShadow: '0 4px 20px rgba(99,91,255,0.3)',
                                    }}
                                >
                                    💳 Continue to Stripe Checkout
                                </a>
                            </>
                        ) : (
                            <div ref={stripeMountRef} style={{ minHeight: stripeLoading ? 0 : '500px' }} />
                        )}

                        <p style={{ margin: 0, fontSize: '0.72rem', color: 'rgba(255,255,255,0.25)', textAlign: 'center' }}>
                            Powered by Stripe. KYC and payment processing handled by Stripe.
                        </p>
                    </div>
                )}

                {/* Step: Direct Solana */}
                {step === 'direct' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <button onClick={() => setStep('choose')} style={backBtnStyle}>
                            ← Back
                        </button>

                        {pool.wallet_address ? (
                            <>
                                {/* QR Code */}
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', display: 'inline-block' }}>
                                        <QRCode value={pool.wallet_address} size={180} />
                                    </div>
                                </div>

                                {/* Address + copy */}
                                <div style={{
                                    background: 'rgba(255,255,255,0.05)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: '12px', padding: '12px 16px',
                                    display: 'flex', alignItems: 'center', gap: '10px',
                                }}>
                                    <code style={{
                                        flex: 1, fontSize: '0.75rem',
                                        color: 'rgba(255,255,255,0.8)',
                                        wordBreak: 'break-all', fontFamily: 'monospace',
                                    }}>
                                        {pool.wallet_address}
                                    </code>
                                    <button
                                        onClick={handleCopy}
                                        style={{
                                            flexShrink: 0,
                                            background: copied ? 'rgba(34,197,94,0.2)' : 'rgba(255,193,7,0.1)',
                                            border: copied ? '1px solid rgba(34,197,94,0.4)' : '1px solid rgba(255,193,7,0.2)',
                                            borderRadius: '8px', padding: '6px 12px',
                                            color: copied ? '#22c55e' : '#ffc107',
                                            fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
                                        }}
                                    >
                                        {copied ? '✓ Copied' : 'Copy'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem', margin: 0 }}>
                                No wallet address assigned to this pool yet.
                            </p>
                        )}

                        {/* Instructions */}
                        <div style={{
                            background: 'rgba(255,255,255,0.03)',
                            border: '1px solid rgba(255,255,255,0.07)',
                            borderRadius: '12px', padding: '16px',
                        }}>
                            <p style={{ margin: '0 0 10px', fontWeight: 700, color: '#fff', fontSize: '0.9rem' }}>
                                How to send:
                            </p>
                            <ol style={{
                                margin: 0, paddingLeft: '18px',
                                color: 'rgba(255,255,255,0.65)',
                                fontSize: '0.85rem', lineHeight: 1.9,
                            }}>
                                <li>Open your Solana wallet app (<strong style={{ color: '#fff' }}>Phantom</strong>, <strong style={{ color: '#fff' }}>Solflare</strong>, or <strong style={{ color: '#fff' }}>Backpack</strong>)</li>
                                <li>Tap <strong style={{ color: '#fff' }}>Send</strong></li>
                                <li>Scan the QR code above, or paste the wallet address</li>
                                <li>Enter the amount of USDC you'd like to contribute</li>
                                <li>Review and confirm the transaction</li>
                            </ol>
                        </div>
                    </div>
                )}

                {/* Step: Contact You Bee */}
                {step === 'contact' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <button onClick={() => setStep('choose')} style={backBtnStyle}>
                            ← Back
                        </button>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🐝</div>
                            <h3 style={{ margin: '0 0 8px', color: '#ffc107', fontSize: '1.2rem', fontWeight: 700 }}>
                                Let's Talk
                            </h3>
                            <p style={{ margin: '0 0 20px', color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem', lineHeight: 1.6 }}>
                                Interested in making a large or custom contribution to the{' '}
                                <strong style={{ color: '#fff' }}>{displayName(pool.name)}</strong> hive?
                                Reach out and we'll make it happen.
                            </p>
                            <a
                                href={`mailto:support@you-bee.com?subject=Contribution%20Inquiry%20%E2%80%94%20${encodeURIComponent(pool.name)}&body=Hi%20You%20Bee%20team%2C%0A%0AI%20am%20interested%20in%20making%20a%20contribution%20to%20the%20${encodeURIComponent(pool.name)}%20hive.%0A%0A`}
                                style={{
                                    display: 'inline-block',
                                    padding: '14px 32px',
                                    borderRadius: '12px',
                                    background: 'linear-gradient(135deg, #ffc107, #ff9800)',
                                    color: '#000',
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    textDecoration: 'none',
                                }}
                            >
                                Email support@you-bee.com
                            </a>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// --- MAIN COMPONENT ---

function formatUsd(usd) {
    if (!usd || usd === 0) return '$0.00';
    for (let d = 2; d <= 8; d++) {
        if (parseFloat(usd.toFixed(d)) !== 0) {
            return `$${usd.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })}`;
        }
    }
    return '<$0.000000001';
}

const STATE_ABBREVS = {
    'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
    'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
    'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID',
    'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
    'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
    'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
    'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
    'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
    'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
    'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
    'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT',
    'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV',
    'Wisconsin': 'WI', 'Wyoming': 'WY', 'District of Columbia': 'DC',
};

function isUserQualified(user, poolName) {
    if (!user) return false;
    if (poolName === 'You Bee General Hive') return true;
    if (poolName.startsWith('Industry: ')) {
        return user.kyc_status === 'Approved' && user.profession === poolName.slice('Industry: '.length);
    }
    if (poolName.startsWith('State: ')) {
        return user.kyc_status === 'Approved' && userAddressMatchesState(user.address, poolName);
    }
    return false;
}

function userAddressMatchesState(userAddress, poolName) {
    if (!userAddress || !poolName.startsWith('State: ')) return false;
    const stateName = poolName.slice('State: '.length);
    if (userAddress.toLowerCase().includes(stateName.toLowerCase())) return true;
    const abbrev = STATE_ABBREVS[stateName];
    if (abbrev) return new RegExp(',\\s*' + abbrev + '(\\s|,|$)', 'i').test(userAddress);
    return false;
}

function Pools() {
    const { user } = useAuth();
    const [pools, setPools]               = useState([]);
    const [poolBalances, setPoolBalances] = useState({});
    const [recipientCounts, setRecipientCounts] = useState({});
    const [dailyRates, setDailyRates]     = useState({});
    const [solPrice, setSolPrice]         = useState(null);
    const [balancesLoading, setBalancesLoading] = useState(false);
    const [activeCategory, setActive]     = useState('All');
    const [search, setSearch]             = useState('');
    const [isLoading, setIsLoading]       = useState(true);
    const [error, setError]               = useState(null);
    const [contributingPool, setContributingPool] = useState(null); // pool object for modal
    const [qualifiedTooltip, setQualifiedTooltip] = useState(null); // pool id or null

    const fetchBalances = async () => {
        setBalancesLoading(true);
        try {
            const res = await api.get('/pools/balances');
            setPoolBalances(res.data);
        } catch {
            // non-fatal — fall back to DB amounts
        } finally {
            setBalancesLoading(false);
        }
    };

    const fetchDailyRates = async () => {
        try {
            const [ratesRes, priceRes, countsRes] = await Promise.all([
                api.get('/pools/daily-rates').catch(() => ({ data: {} })),
                api.get('/sol-price').catch(() => ({ data: { price: null } })),
                api.get('/pools/recipient-counts').catch(() => ({ data: {} })),
            ]);
            setDailyRates(ratesRes.data);
            setSolPrice(priceRes.data.price);
            setRecipientCounts(countsRes.data);
        } catch {
            // non-fatal
        }
    };

    const fetchPools = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await api.get('/pools');
            setPools(res.data);
        } catch (err) {
            setError(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPools();
        fetchBalances();
        fetchDailyRates();
    }, []);

    const handleContributeSuccess = (usdAmount) => {
        setContributingPool(null);
        alert(`Contribution successful! ${usdAmount != null ? `$${parseFloat(usdAmount).toFixed(2)} has been added to this hive.` : ''}`);
        fetchPools();
        fetchBalances();
    };

    const filtered = pools
        .filter(p => {
            const cat = getCategory(p.name);
            const matchCategory = activeCategory === 'All' || cat === activeCategory;
            const matchSearch   = p.name.toLowerCase().includes(search.toLowerCase());
            return matchCategory && matchSearch;
        })
        .sort((a, b) => {
            if (a.name === 'You Bee General Hive') return -1;
            if (b.name === 'You Bee General Hive') return  1;
            const balA = poolBalances[a.id] ?? parseFloat(a.current_amount) ?? 0;
            const balB = poolBalances[b.id] ?? parseFloat(b.current_amount) ?? 0;
            if (balB !== balA) return balB - balA;
            return displayName(a.name).localeCompare(displayName(b.name));
        });

    if (isLoading) {
        return <LoadingSpinner fullPage message="Loading pools..." />;
    }

    if (error) {
        return (
            <div style={{
                minHeight: '100vh',
                background: 'linear-gradient(180deg, #12121a 0%, #1a1a2e 50%, #12121a 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
            }}>
                <div style={{ maxWidth: '400px', width: '100%' }}>
                    <InlineError error={error} onRetry={fetchPools} message="Failed to load pools" />
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(180deg, #12121a 0%, #1a1a2e 50%, #12121a 100%)',
            color: '#ffffff',
            position: 'relative',
            overflowX: 'hidden',
        }}>
            {/* Decorative gradient orbs */}
            <div style={{
                position: 'fixed', top: '-20%', right: '-10%',
                width: '500px', height: '500px',
                background: 'radial-gradient(circle, rgba(255, 193, 7, 0.12) 0%, transparent 70%)',
                borderRadius: '50%', pointerEvents: 'none', zIndex: 0,
            }} />
            <div style={{
                position: 'fixed', bottom: '-30%', left: '-10%',
                width: '400px', height: '400px',
                background: 'radial-gradient(circle, rgba(255, 193, 7, 0.08) 0%, transparent 70%)',
                borderRadius: '50%', pointerEvents: 'none', zIndex: 0,
            }} />

            <div style={{
                padding: '32px 16px', maxWidth: '1200px',
                margin: '0 auto', position: 'relative', zIndex: 1,
                boxSizing: 'border-box', width: '100%',
            }}>
                {/* Page Header */}
                <div style={{ marginBottom: '32px' }}>
                    <h1 style={{
                        fontSize: '2rem', fontWeight: 700, marginBottom: '8px',
                        background: 'linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.7) 100%)',
                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                    }}>
                        Active YouBee Hives
                    </h1>
                    <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.95rem' }}>
                        {filtered.length} hive{filtered.length !== 1 ? 's' : ''} available for contribution
                    </p>
                </div>

                {/* Category Filter Tabs */}
                <style>{`
                    .category-filters { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 24px; }
                    .category-filters button { padding: 10px 20px; }
                    @media (max-width: 480px) { .category-filters button { padding: 8px 14px; font-size: 0.8rem; } }
                `}</style>
                <div className="category-filters">
                    {CATEGORIES.map(cat => (
                        <button
                            key={cat}
                            onClick={() => { setActive(cat); setSearch(''); }}
                            style={{
                                borderRadius: '50px',
                                border: activeCategory === cat ? 'none' : '1px solid rgba(255, 255, 255, 0.15)',
                                background: activeCategory === cat
                                    ? 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)'
                                    : 'rgba(255, 255, 255, 0.05)',
                                color: activeCategory === cat ? '#000' : '#ffffff',
                                fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
                                transition: 'all 0.2s ease', backdropFilter: 'blur(10px)',
                            }}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                {/* Search within category */}
                {activeCategory !== 'All' && (
                    <input
                        type="text"
                        placeholder={`Search ${activeCategory}...`}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{
                            maxWidth: '400px', marginBottom: '28px',
                            padding: '14px 18px', borderRadius: '12px',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            background: 'rgba(255, 255, 255, 0.05)',
                            color: '#ffffff', fontSize: '0.95rem',
                            width: '100%', boxSizing: 'border-box',
                            outline: 'none', transition: 'border-color 0.2s, background 0.2s',
                        }}
                        onFocus={e => { e.target.style.borderColor = 'rgba(255, 193, 7, 0.5)'; e.target.style.background = 'rgba(255, 255, 255, 0.08)'; }}
                        onBlur={e => { e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'; e.target.style.background = 'rgba(255, 255, 255, 0.05)'; }}
                    />
                )}

                {/* Pool Cards */}
                <style>{`
                    .pool-cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 24px; }
                    @media (max-width: 640px) { .pool-cards-grid { grid-template-columns: 1fr; } }
                    .pool-card { min-width: 0; }
                    .pool-wallet-row { flex-wrap: wrap; gap: 6px; }
                    .pool-wallet-row code { min-width: 0; word-break: break-all; }
                    .pool-balance-text { white-space: nowrap; font-size: 0.75rem; }
                    @media (max-width: 400px) {
                        .pool-balance-text { font-size: 0.68rem; }
                    }
                `}</style>
                <div className="pool-cards-grid">
                    {filtered.map(p => {
                        const onChainBalance = poolBalances[p.id] ?? null;
                        const displayBalance = onChainBalance !== null ? onChainBalance : parseFloat(p.current_amount);
                        const TARGET_USD = 100000;
                        // displayBalance is USDC, which equals USD (1:1)
                        const balanceUsd = displayBalance;
                        const pct = Math.min((balanceUsd / TARGET_USD) * 100, 100).toFixed(1);
                        return (
                            <div
                                key={p.id}
                                className="pool-card"
                                style={{
                                    display: 'flex', flexDirection: 'column', gap: '16px',
                                    position: 'relative',
                                    background: 'rgba(255, 255, 255, 0.03)',
                                    border: '1px solid rgba(255, 255, 255, 0.08)',
                                    borderRadius: '20px', backdropFilter: 'blur(20px)',
                                    padding: '24px', transition: 'border-color 0.2s, transform 0.2s',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255, 193, 7, 0.3)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                            >
                                {/* Header */}
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                        <span style={{
                                            fontSize: '0.7rem', fontWeight: 700,
                                            textTransform: 'uppercase', letterSpacing: '0.08em', color: '#ffc107',
                                        }}>
                                            {getCategory(p.name) === 'All' ? 'General' : getCategory(p.name).replace('Top ', '')}
                                        </span>
                                        {isUserQualified(user, p.name) && (
                                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                                <span style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                    padding: '2px 8px', borderRadius: '20px',
                                                    background: 'rgba(34, 197, 94, 0.15)',
                                                    border: '1px solid rgba(34, 197, 94, 0.35)',
                                                    color: '#22c55e', fontSize: '0.68rem', fontWeight: 700,
                                                }}>
                                                    ✓ Qualified
                                                </span>
                                                <span
                                                    onMouseEnter={() => setQualifiedTooltip(p.id)}
                                                    onMouseLeave={() => setQualifiedTooltip(null)}
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                        width: '14px', height: '14px', borderRadius: '50%',
                                                        background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                                                        color: 'rgba(255,255,255,0.5)', fontSize: '0.6rem', fontWeight: 700,
                                                        cursor: 'default', flexShrink: 0,
                                                    }}
                                                >?</span>
                                                {qualifiedTooltip === p.id && (
                                                    <div style={{
                                                        position: 'absolute', right: 0, top: '22px',
                                                        zIndex: 100, width: '240px',
                                                        background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.12)',
                                                        borderRadius: '10px', padding: '12px 14px',
                                                        boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                                                        fontSize: '0.78rem', color: 'rgba(255,255,255,0.8)',
                                                        fontWeight: 400, lineHeight: 1.55,
                                                    }}>
                                                        <strong style={{ color: '#22c55e', display: 'block', marginBottom: '4px' }}>You're receiving distributions</strong>
                                                        You will automatically receive hourly distributions from this hive, sent directly to your You Bee wallet.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <h3 style={{
                                        margin: '6px 0 0', fontSize: '1.1rem', fontWeight: 600, color: '#ffffff',
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>
                                        {displayName(p.name)}
                                    </h3>
                                </div>

                                <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', lineHeight: 1.5 }}>
                                    {p.description}
                                </p>

                                {/* Progress bar */}
                                <div>
                                    <div style={{ background: 'rgba(255, 255, 255, 0.1)', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                                        <div style={{
                                            width: `${pct}%`,
                                            background: 'linear-gradient(90deg, #ffc107 0%, #ff9800 100%)',
                                            height: '100%', borderRadius: '4px',
                                            transition: 'width 0.4s ease', boxShadow: '0 0 10px rgba(255, 193, 7, 0.4)',
                                        }} />
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', gap: '8px' }}>
                                        <span style={{ fontSize: '0.85rem', color: '#ffc107', fontWeight: 600, flexShrink: 0 }}>{pct}%</span>
                                        <span className="pool-balance-text" style={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'right' }}>
                                            {balancesLoading && onChainBalance === null ? '…' :
                                                `$${balanceUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDC`
                                            }
                                            {' '}/ $100k
                                        </span>
                                    </div>
                                </div>

                                {/* Daily rate + recipient count */}
                                {(() => {
                                    const rate = dailyRates[p.name];
                                    const usd = rate ?? null; // rate is already in USDC (= USD)
                                    const count = recipientCounts[p.id] ?? null;
                                    return (
                                        <>
                                            {usd > 0 && (
                                                <div style={{
                                                    display: 'flex', alignItems: 'center', gap: '6px',
                                                    fontSize: '0.8rem', color: 'rgba(34,197,94,0.85)', fontWeight: 600,
                                                }}>
                                                    <span>🐝</span>
                                                    <span>~{formatUsd(usd)}/day per recipient</span>
                                                </div>
                                            )}
                                            {count > 0 && (
                                                <div style={{
                                                    fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)', fontWeight: 500,
                                                }}>
                                                    {count.toLocaleString()} eligible member{count !== 1 ? 's' : ''}
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}

                                {/* Wallet address row */}
                                {p.wallet_address && (
                                    <div className="pool-wallet-row" style={{
                                        display: 'flex', alignItems: 'center', gap: '8px',
                                        padding: '10px 12px',
                                        background: 'rgba(255, 255, 255, 0.03)',
                                        borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.06)',
                                    }}>
                                        <span style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Wallet
                                        </span>
                                        <code style={{ fontSize: '0.78rem', color: 'rgba(255, 255, 255, 0.7)', fontFamily: 'monospace' }}>
                                            {p.wallet_address.slice(0, 6)}…{p.wallet_address.slice(-6)}
                                        </code>
                                        <button
                                            onClick={() => navigator.clipboard.writeText(p.wallet_address)}
                                            style={{
                                                marginLeft: 'auto',
                                                background: 'rgba(255, 193, 7, 0.1)',
                                                border: '1px solid rgba(255, 193, 7, 0.2)',
                                                borderRadius: '6px', padding: '8px 14px',
                                                fontSize: '0.8rem', cursor: 'pointer',
                                                color: '#ffc107', fontWeight: 600,
                                                transition: 'all 0.2s', minHeight: '36px',
                                            }}
                                            onMouseEnter={e => { e.target.style.background = 'rgba(255, 193, 7, 0.2)'; }}
                                            onMouseLeave={e => { e.target.style.background = 'rgba(255, 193, 7, 0.1)'; }}
                                            title="Copy full wallet address"
                                        >
                                            Copy
                                        </button>
                                    </div>
                                )}

                                {/* KYC notice for matching state pools */}
                                {user?.kyc_status !== 'Approved' && userAddressMatchesState(user?.address, p.name) && (
                                    <div style={{
                                        padding: '10px 14px',
                                        background: 'rgba(255, 193, 7, 0.08)',
                                        border: '1px solid rgba(255, 193, 7, 0.25)',
                                        borderRadius: '10px',
                                        fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.5,
                                    }}>
                                        <strong style={{ color: '#ffc107' }}>KYC required</strong> — Complete identity verification to receive distributions from this hive.{' '}
                                        <Link to="/profile" style={{ color: '#ffc107', textDecoration: 'underline' }}>Verify now →</Link>
                                    </div>
                                )}

                                {/* Contribute Now button */}
                                <button
                                    onClick={() => setContributingPool(p)}
                                    style={{
                                        width: '100%', marginTop: 'auto',
                                        padding: '14px 24px', borderRadius: '12px', border: 'none',
                                        background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                                        color: '#000', fontWeight: 700, fontSize: '0.95rem',
                                        cursor: 'pointer', boxShadow: '0 4px 20px rgba(255, 193, 7, 0.3)',
                                        transition: 'all 0.2s ease',
                                    }}
                                    onMouseEnter={e => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 6px 25px rgba(255, 193, 7, 0.4)'; }}
                                    onMouseLeave={e => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 20px rgba(255, 193, 7, 0.3)'; }}
                                >
                                    Contribute Now
                                </button>
                            </div>
                        );
                    })}

                    {filtered.length === 0 && (
                        <div style={{
                            gridColumn: '1 / -1', textAlign: 'center', padding: '60px 20px',
                            background: 'rgba(255, 255, 255, 0.03)',
                            borderRadius: '20px', border: '1px solid rgba(255, 255, 255, 0.08)',
                        }}>
                            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '1rem' }}>
                                No hives found matching your criteria.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Contribute Modal */}
            {contributingPool && (
                <ContributeModal
                    pool={contributingPool}
                    onClose={() => setContributingPool(null)}
                    onSuccess={handleContributeSuccess}
                    solPrice={solPrice}
                />
            )}


        </div>
    );
}

export default Pools;
